import { CostAdjustmentScope } from './CostAdjustmentScope';
import { useState } from 'react';
import { SlidersHorizontalIcon } from '@phosphor-icons/react';
import { useGT } from 'gt-react';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/components/ui/popover';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { toast } from 'src/components/ui/toast';
import {
  useSetRevisedUnitCosts,
  type AssignmentLine,
} from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';
import { planRevisedCosts } from '../utils/lineBuyTerms';
import {
  ADJUST_KINDS,
  adjustmentFrom,
  isAdjustableLine,
  isAdjustKind,
  linesInScope,
  type AdjustKind,
  type AdjustScope,
} from '../utils/revisedCostAdjust';
import { useLineBulkEditTargets } from './lineBulkEditContext';

/**
 * Adjust the revised cost on one line, the highlighted lines, or every buy
 * line of the order (GEN-3141): a percentage off or on the buyer cost, one
 * set cost, or a reset back to the buyer cost.
 */
export function RevisedCostAdjust({
  line,
  getOrderLines,
  disabled,
}: {
  line: AssignmentLine;
  getOrderLines: () => readonly AssignmentLine[];
  /** Released or saving: nothing would save. */
  disabled: boolean;
}) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={gt('Adjust revised cost', {
            context:
              'buyer board button: percentage discount, markup, set or reset revised cost',
          })}
          title={gt('Adjust revised cost', {
            context:
              'buyer board button: percentage discount, markup, set or reset revised cost',
          })}
          disabled={disabled}
          onClick={(event) => event.stopPropagation()}
          className="shrink-0 rounded p-0.5 disabled:pointer-events-none disabled:opacity-50 text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-1 focus-visible:ring-ring"
        >
          <SlidersHorizontalIcon className="size-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-3"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        <AdjustForm
          line={line}
          getOrderLines={getOrderLines}
          onDone={() => setOpen(false)}
        />
      </PopoverContent>
    </Popover>
  );
}

// Mounted only while the popover is open, so each opening starts fresh.
function AdjustForm({
  line,
  getOrderLines,
  onDone,
}: {
  line: AssignmentLine;
  getOrderLines: () => readonly AssignmentLine[];
  onDone: () => void;
}) {
  const gt = useGT();
  // The form mounts only while the popover is open, so cells that are not
  // being adjusted never read the cart's lines.
  const orderLines = getOrderLines();
  const selectedIds = useLineBulkEditTargets()(line.suggestionId);
  const [kind, setKind] = useState<AdjustKind>('decrease');
  const [text, setText] = useState('');
  const [scope, setScope] = useState<AdjustScope>(
    selectedIds.length > 1 ? 'selected' : 'line',
  );
  const { mutate, isPending } = useSetRevisedUnitCosts();
  const adjustment = adjustmentFrom(kind, text);

  const apply = () => {
    if (!adjustment) return;
    const plan = planRevisedCosts(
      linesInScope(scope, line, selectedIds, orderLines),
      adjustment,
    );
    if (plan.skipped > 0) {
      toast.warning(
        plan.skipped === 1
          ? gt('1 line has no buyer cost to adjust and was left as is.', {
              context: 'revised cost percentage adjust skipped line, singular',
            })
          : gt('{n} lines have no buyer cost to adjust and were left as is.', {
              context: 'revised cost percentage adjust skipped lines',
              n: plan.skipped,
            }),
      );
    }
    if (plan.costs.length === 0) return;
    mutate(
      { costs: plan.costs },
      {
        onSuccess: onDone,
        onError: () => {
          toast.error(
            gt('Could not adjust the revised cost. Try again.', {
              context: 'revised cost adjust error',
            }),
          );
        },
      },
    );
  };

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        apply();
      }}
    >
      <KindToggle kind={kind} onChange={setKind} />
      <ValueField kind={kind} text={text} onChange={setText} />
      <CostAdjustmentScope
        scope={scope}
        onChange={setScope}
        selectedCount={selectedIds.length}
        orderBuyCount={orderLines.filter(isAdjustableLine).length}
      />
      <Button
        type="submit"
        size="sm"
        disabled={!adjustment || isPending}
        className="h-7 text-xs"
      >
        {gt('Apply', { context: 'revised cost adjust apply button' })}
      </Button>
    </form>
  );
}

function KindToggle({
  kind,
  onChange,
}: {
  kind: AdjustKind;
  onChange: (kind: AdjustKind) => void;
}) {
  const gt = useGT();
  const labels: Record<AdjustKind, string> = {
    decrease: gt('% off', {
      context: 'revised cost adjust: percentage decrease',
    }),
    increase: gt('% on', {
      context: 'revised cost adjust: percentage increase',
    }),
    set: gt('Set', { context: 'revised cost adjust: one set cost' }),
    reset: gt('Reset', {
      context: 'revised cost adjust: back to the buyer (system) cost',
    }),
  };
  return (
    <ToggleGroup
      type="single"
      size="sm"
      value={kind}
      onValueChange={(value) => {
        if (isAdjustKind(value)) onChange(value);
      }}
      className="h-7 w-full"
    >
      {ADJUST_KINDS.map((value) => (
        <ToggleGroupItem
          key={value}
          value={value}
          className="h-7 flex-1 text-xs"
        >
          {labels[value]}
        </ToggleGroupItem>
      ))}
    </ToggleGroup>
  );
}

function ValueField({
  kind,
  text,
  onChange,
}: {
  kind: AdjustKind;
  text: string;
  onChange: (text: string) => void;
}) {
  const gt = useGT();
  if (kind === 'reset') {
    return (
      <p className="text-xs text-muted-foreground">
        {gt('Clears the revised cost; the line buys at the buyer cost.', {
          context: 'revised cost reset explanation',
        })}
      </p>
    );
  }
  const isSet = kind === 'set';
  return (
    <div className="flex flex-col gap-1">
      <Input
        autoFocus
        inputMode="decimal"
        value={text}
        onChange={(event) => onChange(event.target.value)}
        aria-label={
          isSet
            ? gt('Revised cost', {
                context: 'revised cost adjust input: one unit cost',
              })
            : gt('Percent', {
                context: 'revised cost adjust input: percentage',
              })
        }
        placeholder={isSet ? '0.0000' : '10'}
        className="h-7 text-right text-xs tabular-nums"
      />
      {!isSet && (
        <p className="text-xs text-muted-foreground">
          {gt('Of the buyer cost, rounded to the cent (half to even).', {
            context: 'revised cost percentage adjust explanation',
          })}
        </p>
      )}
    </div>
  );
}
